import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Dialog,
  DialogHeader,
  DialogContent,
  List,
  ListItem,
  Button,
  DialogFooter,
} from '@taopix/taopix-design-system';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import useSettings from '../../Hooks/useSettings';
import {CoreEvent, Lang} from '@taopix/taopix-core';
import {useTaopix} from '../Context/TaopixContext';
import {LocaleKey} from '@taopix/taopix-core/dist/types/types';

export type LanguageDialogProps = {
  isOpen: boolean;
  onChangeLanguage?: (lang: string) => void;
};

const LanguageDialog = ({isOpen, onChangeLanguage}: LanguageDialogProps) => {
  const {t, i18n} = useTranslation();
  const settings = useSettings();
  const ui = useUiState();
  const taopix = useTaopix();

  const current = i18n.language || localStorage.getItem('i18nextLng');

  useEffect(() => {
    i18n.on('languageChanged', () => {
      ui.dispatch(UserInterfaceActions.setIsBusy(false));
      document.getElementById('languageDialogCloseButton')?.focus();
    });
  }, []);

  /**
   * On change language, update the core strings
   */
  const changeLanguage = React.useCallback(
    (lang: string) => {
      // change the core language
      if (!i18n.languages.includes(lang)) ui.dispatch(UserInterfaceActions.setIsBusy(true));

      Lang.hydrate(taopix.core.get('server'), lang as LocaleKey);
      setTimeout(() => {
        PubSub.publish(`${CoreEvent.RELOAD_PAGE}.${ui.state.pageNumber}`);
      }, 2000);
    },
    [taopix.core.get('server'), ui.state.pageNumber, i18n.languages]
  );

  const onClick = (label: string, value: string, index: number, event: React.MouseEvent) => {
    const selected = value.split(' ')[0];
    if (current !== selected) {
      i18n.changeLanguage(selected);

      if (typeof onChangeLanguage === 'function') {
        onChangeLanguage(selected);
      } else {
        changeLanguage(selected);
      }
    }
  };

  const closeDialog = () => {
    ui.dispatch(UserInterfaceActions.setLanguageDialogOpen(false));
  };

  // get our available languages from the app context
  const langs = settings.state.locale?.available || {};

  return (
    <Dialog
      open={isOpen}
      width={300}
      height={500}
      theme={'tpx-theme-dialogs'}
      themeTip="Dialogs"
      id={'language-dialog'}
      onClose={closeDialog}
      title={t('str_TitleLanguages')}
    >
      <DialogContent>
        <div data-testid={'tpx-lang-switcher'}>
          <ul className="flex flex-col" role="group">
            {Object.keys(langs).map((code: string, index: number) => {
              return (
                <li key={index} className="flex flex-col">
                  <Button
                    label={langs[code]}
                    buttonStyle={'standard'}
                    aria-selected={code === current ? true : false}
                    corners={'square'}
                    labelAlignment={'left'}
                    onClick={e => onClick(langs[code], code, index, e)}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      </DialogContent>
      <DialogFooter isSmallScreen={ui.state.isSmallScreen}>
        <Button
          id="languageDialogCloseButton"
          label={t('str_ButtonClose')}
          buttonStyle="negative"
          onClick={closeDialog}
        />
      </DialogFooter>
    </Dialog>
  );
};

export default LanguageDialog;
